import {
    SECTION_SCOPE,
    QUESTION_SCOPE,
    ACTION_TYPES
} from '@/utils/constants/constants.js';

export const transition_conditions_mixin = {
    data: () => ({
        condition_structure: {
            action_type: '',
            target_scope: '',
            target_type: '',
            section_scope: '',
            selected_sections: [],
            transition_types: [],
            question_scope: '',
            selected_sections_for_questions: [],
            selected_questions: [],
            completion_type: '',
            approvals_required: 1
        },
        edit_transition_type: '',
        edit_match_requirement: 'All conditions',
        edit_condition: {
            type: '',
            role: '',
            approvals_required: 0
        }
    }),

    methods: {

        create_empty_condition() {
            const condition_data = {
                action_type: '',
                selected_action: [],
                action_search: '',
                section_scope: 'all',
                selected_sections: [],
                question_scope: 'all',
                selected_sections_for_questions: [],
                selected_questions: [],
                required_role: '',
                completion_type: 'complete',
                approvals_required: 1
            };

            return condition_data;
        },

        add_condition_to_reviewer(reviewer) {
            if (!reviewer.conditions) {
                this.$set(reviewer, 'conditions', []);
            }

            const new_condition = this.create_empty_condition();
            reviewer.conditions.push(new_condition);
        },
        add_condition_in_edit() {
            const reviewer = Reflect.get(this, 'editing_rule');
            if (!reviewer) return;

            this.add_condition_to_reviewer(reviewer);
        },

        remove_condition_in_edit(condition_index) {
            if (!Reflect.get(this, 'editing_rule')) return;

            const reviewer = Reflect.get(this, 'editing_rule');
            const conditions = Reflect.get(reviewer, 'conditions');

            if (conditions && conditions.length > condition_index) {
                conditions.splice(condition_index, 1);
            }
        },

        is_valid_reviewer_index(reviewers, reviewer_index) {
            return Array.isArray(reviewers) &&
                typeof reviewer_index === 'number' &&
                reviewer_index >= 0 &&
                reviewer_index < reviewers.length &&
                Reflect.has(reviewers, reviewer_index);
        },

        is_valid_reviewer_object(reviewer) {
            return typeof reviewer === 'object' && reviewer !== null;
        },

        ensure_conditions_array(reviewer) {
            if (!Reflect.has(reviewer, 'conditions') || !Array.isArray(reviewer.conditions)) {
                this.$set(reviewer, 'conditions', []);
            }
        },

        toggle_condition_ui_for_reviewer(status, reviewer_index) {
            if (typeof status !== 'object' || status === null) return;

            const reviewers = Reflect.get(status, 'reviewers');
            if (!this.is_valid_reviewer_index(reviewers, reviewer_index)) return;

            const reviewer = Reflect.get(reviewers, reviewer_index);
            if (!this.is_valid_reviewer_object(reviewer)) return;

            this.ensure_conditions_array(reviewer);

            const current_state = Reflect.get(reviewer, 'show_condition_ui');
            Reflect.set(reviewer, 'show_condition_ui', !current_state);

            if (Reflect.get(reviewer, 'show_condition_ui') && reviewer.conditions.length === 0) {
                this.add_condition_for_reviewer(reviewer);
            }
        },

        add_condition_for_reviewer(reviewer) {
            this.add_condition_to_reviewer(reviewer);
        },

        on_action_type_change(condition) {
            Reflect.set(condition, 'selected_action', []);
            Reflect.set(condition, 'action_search', '');
            Reflect.set(condition, 'section_scope', SECTION_SCOPE.ALL);
            Reflect.set(condition, 'selected_sections', []);
            Reflect.set(condition, 'question_scope', QUESTION_SCOPE.ALL);
            Reflect.set(condition, 'selected_questions', []);
            Reflect.set(condition, 'selected_sections_for_questions', []);
            Reflect.set(condition, 'required_role', '');
            Reflect.set(condition, 'approvals_required', 1);
        },

        get_filtered_actions(condition) {
            const actions_list = this.actions_list || [];
            if (!condition.action_search || condition.action_search.trim() === '') {
                const filtered_actions_result = actions_list;
                return filtered_actions_result;
            }

            const search_term = condition.action_search.toLowerCase().trim();
            const filtered_actions = actions_list.filter(action => {
                return (
                    (action.name && action.name.toLowerCase().includes(search_term)) ||
                    (action.description && action.description.toLowerCase().includes(search_term))
                );
            });
            return filtered_actions;
        },

        get_action_name_by_id(action_id) {
            if (!action_id) {
                const empty_string_result = '';
                return empty_string_result;
            }
            const actions_list = this.actions_list || [];
            const action = actions_list.find(a => a.id === action_id);
            const action_name_result = action ? action.name : `Action ${action_id}`;
            return action_name_result;
        },

        select_all_actions(condition) {
            const filtered_actions = this.get_filtered_actions(condition);
            const filtered_ids = filtered_actions.map(action => action.id);

            if (!condition.selected_action) {
                condition.selected_action = [];
            }

            filtered_ids.forEach(id => {
                if (!condition.selected_action.includes(id)) {
                    condition.selected_action.push(id);
                }
            });
        },

        deselect_all_actions(condition) {
            if (condition.selected_action) {
                condition.selected_action = [];
            }
        },

        get_selected_action_description(action_ids) {
            if (!action_ids || action_ids.length === 0) {
                const empty_description_result = '';
                return empty_description_result;
            }
            const actions_list = Reflect.get(this, 'actions_list');
            const ids = Array.isArray(action_ids) ? action_ids : [action_ids];

            const descriptions = ids.map(id => {
                const action = actions_list.find(a => Reflect.get(a, 'id') === id);
                return action ? Reflect.get(action, 'description') : '';
            }).filter(desc => desc !== '');

            const action_description_result = descriptions.length > 0 ? descriptions.join(', ') : '';
            return action_description_result;
        },

        get_action_names_by_ids(action_ids) {
            if (!action_ids || Reflect.get(action_ids, 'length') === 0) {
                return [];
            }

            const actions_list = Reflect.get(this, 'actions_list');
            const ids = Array.isArray(action_ids)
                ? action_ids
                : [Reflect.get({ value: action_ids }, 'value')];

            const get_action_name = id => {
                const action = actions_list.find(a => Reflect.get(a, 'id') === id);
                return action ? Reflect.get(action, 'name') : '';
            };

            return ids.map(get_action_name).filter(name => name !== '');
        },

        remove_condition(reviewer, condition_index) {
            const conditions = Reflect.get(reviewer, 'conditions');
            if (conditions && conditions.length > condition_index) {
                conditions.splice(condition_index, 1);
            }
        },

        ensure_numeric_sections(condition) {
            const selected_sections = Reflect.get(condition, 'selected_sections');
            if (selected_sections && selected_sections.length > 0) {
                const processed_sections = selected_sections.map(id => {
                    const num_id = parseInt(id, 10);
                    return isNaN(num_id) ? id : num_id;
                }).filter(id => id !== null && id !== undefined);
                Reflect.set(condition, 'selected_sections', processed_sections);
            }
            return condition;
        },

        prepare_conditions_payload(reviewer) {
            const conditions_payload = [];
            const conditions = Reflect.get(reviewer, 'conditions') || [];

            const build_base_payload = condition => {
                const selected_actions_array = this.normalize_to_array(
                    condition.selected_actions || condition.selected_action || []
                );

                return {
                    action_type: condition.action_type,
                    selected_actions: selected_actions_array,
                    required_role: condition.required_role || '',
                    section_scope: condition.section_scope || 'all',
                    question_scope: condition.question_scope || 'all',
                    completion_type: condition.completion_type || 'complete',
                    approvals_required: condition.approvals_required || 1,
                    action: this.get_action_names_by_ids(selected_actions_array).join(', ') || condition.action_type || ''
                };
            };

            const apply_type_overrides = (payload, condition) => {
                const type = condition.action_type;

                if (type === 'section_completion') {
                    payload.selected_sections = this.normalize_to_array(condition.selected_sections || []);
                    payload.selected_questions = [];
                    payload.selected_sections_for_questions = [];
                    return;
                }

                if (type === 'question_completion') {
                    payload.selected_sections_for_questions = this.normalize_to_array(
                        condition.selected_sections_for_questions || []
                    );
                    payload.selected_questions = this.normalize_to_array(condition.selected_questions || []);
                    payload.selected_sections = [];
                    return;
                }

                if (type === 'approval') {
                    payload.selected_sections = [];
                    payload.selected_questions = [];
                    payload.selected_sections_for_questions = [];
                }
            };

            conditions.forEach(condition => {
                const payload = build_base_payload(condition);
                apply_type_overrides(payload, condition);
                conditions_payload.push(payload);
            });

            return conditions_payload;
        },

        build_condition_payload_with_defaults(condition) {
            const selected_actions_array = this.normalize_to_array(condition.selected_action);
            const payload = {
                action_type: condition.action_type,
                selected_actions: selected_actions_array,
                required_role: condition.required_role || '',
                section_scope: condition.section_scope || 'all',
                question_scope: condition.question_scope || 'all',
                completion_type: condition.completion_type || 'complete',
                approvals_required: condition.approvals_required || 1,
                action: this.get_action_names_by_ids(selected_actions_array).join(', ') || condition.action_type || ''
            };
            return payload;
        },

        enrich_payload_by_action_type(payload, condition) {
            const action_type = condition.action_type;

            if (action_type === 'section_completion') {
                this.set_section_completion_fields(payload, condition);
            } else if (action_type === 'question_completion') {
                this.set_question_completion_fields(payload, condition);
            } else if (action_type === 'approval') {
                this.set_approval_fields(payload);
            }
        },

        set_section_completion_fields(payload, condition) {
            Reflect.set(payload, 'selected_sections', this.normalize_to_array(condition.selected_sections));
            Reflect.set(payload, 'selected_questions', []);
            Reflect.set(payload, 'selected_sections_for_questions', []);
        },

        set_question_completion_fields(payload, condition) {
            const sections_for_questions = condition.selected_sections_for_questions && condition.selected_sections_for_questions.length > 0
                ? condition.selected_sections_for_questions
                : this.normalize_to_array(condition.selected_section_for_questions);

            Reflect.set(payload, 'selected_sections_for_questions', sections_for_questions);
            Reflect.set(payload, 'selected_questions', this.normalize_to_array(condition.selected_questions));
            Reflect.set(payload, 'selected_sections', []);
        },

        set_approval_fields(payload) {
            Reflect.set(payload, 'selected_sections', []);
            Reflect.set(payload, 'selected_questions', []);
            Reflect.set(payload, 'selected_sections_for_questions', []);
        },

        normalize_to_array(value) {
            if (Array.isArray(value)) return value.slice();
            if (value === null || value === undefined || value === '') return [];
            try {
                return Reflect.construct(Array, [value]);
            } catch (e) {
                return Array.of(value);
            }
        },

        build_condition_payload(condition) {
            const condition_payload = {
                action_type: Reflect.get(condition, 'action_type'),
                action: this.get_action_name(condition),
                selected_actions: this.get_selected_actions_array(condition),
                section_scope: Reflect.get(condition, 'section_scope'),
                selected_sections: this.process_id_array(Reflect.get(condition, 'selected_sections')),
                question_scope: Reflect.get(condition, 'question_scope'),
                selected_questions: this.process_id_array(Reflect.get(condition, 'selected_questions')),
                selected_sections_for_questions: this.process_id_array(Reflect.get(condition, 'selected_sections_for_questions')),
                required_role: this.process_required_role(condition),
                completion_type: Reflect.get(condition, 'completion_type'),
                approvals_required: Reflect.get(condition, 'approvals_required') || 1
            };
            return condition_payload;
        },

        get_selected_actions_array(condition) {
            const selected_action = Reflect.get(condition, 'selected_action');
            if (!selected_action) {
                const empty_selected_actions_result = [];
                return empty_selected_actions_result;
            }

            if (Array.isArray(selected_action)) {
                const array_selected_actions_result = this.process_id_array(selected_action);
                return array_selected_actions_result;
            }

            const action_id = parseInt(selected_action, 10);
            if (isNaN(action_id)) {
                const invalid_action_id_result = [];
                return invalid_action_id_result;
            }
            const single_action_id_result = Reflect.get([action_id], 0) !== undefined ? [action_id] : [];
            return single_action_id_result;
        },

        process_id_array(id_array) {
            if (!id_array) {
                const empty_id_array_result = [];
                return empty_id_array_result;
            }

            if (Array.isArray(id_array)) {
                const processed_array = id_array
                    .map(id => {
                        const num_id = parseInt(id, 10);
                        return isNaN(num_id) ? null : num_id;
                    })
                    .filter(id => id !== null && id !== undefined);
                const filtered_id_array_result = processed_array;
                return filtered_id_array_result;
            }

            const num_id = parseInt(id_array, 10);
            const single_id_array_result = isNaN(num_id) ? [] : [num_id];
            return single_id_array_result;
        },

        get_action_name(condition) {
            const selected_action = Reflect.get(condition, 'selected_action');
            if (selected_action) {
                const actions_list = Reflect.get(this, 'actions_list');

                if (Array.isArray(selected_action)) {
                    const action_names = selected_action.map(id => {
                        const action = actions_list.find(a => Reflect.get(a, 'id') === parseInt(id, 10));
                        return action ? Reflect.get(action, 'name') : '';
                    }).filter(name => name !== '');

                    const multiple_action_names_result = action_names.length > 0 ? action_names.join(', ') : '';
                    return multiple_action_names_result;
                } else {
                    const selected_action_obj = actions_list.find(action => Reflect.get(action, 'id') === parseInt(selected_action, 10));
                    const single_action_name_result = selected_action_obj ? Reflect.get(selected_action_obj, 'name') : '';
                    return single_action_name_result;
                }
            }
            const fallback_action_name_result = Reflect.get(condition, 'action_type') || '';
            return fallback_action_name_result;
        },

        process_required_role(condition) {
            if (Reflect.get(condition, 'action_type') === ACTION_TYPES.APPROVAL) {
                const required_role = Reflect.get(condition, 'required_role');
                if (required_role) {
                    const role_id = parseInt(required_role, 10);
                    const parsed_role_result = isNaN(role_id) ? required_role : role_id;
                    return parsed_role_result;
                }
            }
            const empty_role_result = '';
            return empty_role_result;
        },

        validate_individual_condition(condition) {
            const validations = [
                () => this.validate_action_selected(condition),
                () => this.validate_section_completion(condition),
                () => this.validate_question_completion(condition),
                () => this.validate_approval(condition)
            ];

            for (const validation of validations) {
                const result = validation();
                if (!result.valid) return result;
            }

            const validation_result = { valid: true };
            return validation_result;
        },

        validate_action_selected(condition) {
            const selected_actions = Reflect.get(condition, 'selected_actions');
            if (!selected_actions || selected_actions.length === 0) {
                const invalid_action_selection_result = { valid: false, message: 'Please select at least one action for the condition' };
                return invalid_action_selection_result;
            }
            const valid_action_selection_result = { valid: true };
            return valid_action_selection_result;
        },

        validate_section_completion(condition) {
            if (Reflect.get(condition, 'action_type') !== ACTION_TYPES.SECTION_COMPLETION) {
                const non_section_completion_result = { valid: true };
                return non_section_completion_result;
            }

            const section_scope = Reflect.get(condition, 'section_scope');
            if ([SECTION_SCOPE.SPECIFIC, SECTION_SCOPE.ONE].includes(section_scope)) {
                const selected_sections = Reflect.get(condition, 'selected_sections');
                if (!selected_sections || selected_sections.length === 0) {
                    const invalid_section_selection_result = { valid: false, message: 'Please select at least one section for section completion condition' };
                    return invalid_section_selection_result;
                }
            }
            const valid_section_completion_result = { valid: true };
            return valid_section_completion_result;
        },

        validate_question_completion(condition) {
            if (Reflect.get(condition, 'action_type') !== ACTION_TYPES.QUESTION_COMPLETION) {
                const non_question_completion_result = { valid: true };
                return non_question_completion_result;
            }

            const question_scope = Reflect.get(condition, 'question_scope');
            if ([QUESTION_SCOPE.SPECIFIC, QUESTION_SCOPE.ONE].includes(question_scope)) {
                const selected_sections = Reflect.get(condition, 'selected_sections_for_questions');
                if (!selected_sections || selected_sections.length === 0) {
                    const invalid_section_for_questions_result = { valid: false, message: 'Please select at least one section to choose questions from' };
                    return invalid_section_for_questions_result;
                }

                const selected_questions = Reflect.get(condition, 'selected_questions');
                if (!selected_questions || selected_questions.length === 0) {
                    const invalid_question_selection_result = { valid: false, message: 'Please select at least one question for question completion condition' };
                    return invalid_question_selection_result;
                }
            }
            const valid_question_completion_result = { valid: true };
            return valid_question_completion_result;
        },

        validate_approval(condition) {
            if (Reflect.get(condition, 'action_type') !== ACTION_TYPES.APPROVAL) {
                const non_approval_result = { valid: true };
                return non_approval_result;
            }

            if (!Reflect.get(condition, 'required_role')) {
                const missing_role_result = { valid: false, message: 'Please select a required role for approval condition' };
                return missing_role_result;
            }

            const approvals_required = Reflect.get(condition, 'approvals_required');
            if (!approvals_required || approvals_required < 1) {
                const invalid_approvals_result = { valid: false, message: 'Please set at least 1 approval required for approval condition' };
                return invalid_approvals_result;
            }
            const valid_approval_result = { valid: true };
            return valid_approval_result;
        },

        on_section_scope_change(condition) {
            if (Reflect.get(condition, 'section_scope') === SECTION_SCOPE.ALL) {
                Reflect.set(condition, 'selected_sections', []);
            }
        },

        on_question_scope_change(condition) {
            const question_scope = Reflect.get(condition, 'question_scope');
            if (question_scope === QUESTION_SCOPE.ALL || question_scope === QUESTION_SCOPE.MANDATORY) {
                Reflect.set(condition, 'selected_questions', []);
                Reflect.set(condition, 'selected_sections_for_questions', []);
            }
        }
    }
};